/**
 * Document Service
 * Handles document management, versioning, and sharing
 */

import { Document, DocumentFormData, FileType, Task, Project } from '@/types'

interface MinimalTask {
  id: string
  name: string
  description: string
}

interface MinimalProject {
  id: string
  name: string
  description: string
}
import { BaseService } from './base.service'
import { StorageKeys } from './storage'
import { cleanUser, cleanWorkspace } from '@/lib/utils/clean-references'

class DocumentService extends BaseService<Document> {
  constructor() {
    super(StorageKeys.DOCUMENTS)
  }

  /**
   * Create document with relationships
   */
  async createDocument(data: DocumentFormData, creatorId: string): Promise<Document> {
    const { userService } = await import('./user.service')
    const { workspaceService } = await import('./workspace.service')
    const { projectService } = await import('./project.service')
    const { taskService } = await import('./task.service')

    const creator = await userService.getById(creatorId)
    if (!creator) {
      throw new Error('Creator not found')
    }

    const workspace = await workspaceService.getById(data.workspaceId)
    if (!workspace) {
      throw new Error('Workspace not found')
    }

    // In a real app, would handle file upload
    const url = data.file ? await this.uploadFile(data.file) : data.url

    const type = data.file ? this.getFileType(data.file) : FileType.Other

    // Load associated nodes if provided
    const users = [cleanUser(creator)]
    if (data.userIds && data.userIds.length > 0) {
      const additionalUsers = await Promise.all(data.userIds.map((id) => userService.getById(id)))
      additionalUsers.forEach((u) => {
        if (u && !users.some((existing) => existing.id === u.id)) {
          users.push(cleanUser(u))
        }
      })
    }

    const projects: MinimalProject[] = []
    if (data.projectIds && data.projectIds.length > 0) {
      const projectNodes = await Promise.all(
        data.projectIds.map((id) => projectService.getById(id))
      )
      projectNodes.forEach((p) => {
        if (p) {
          projects.push({
            id: p.id,
            name: p.name,
            description: p.description,
          })
        }
      })
    }

    const tasks: MinimalTask[] = []
    if (data.taskIds && data.taskIds.length > 0) {
      const taskNodes = await Promise.all(data.taskIds.map((id) => taskService.getById(id)))
      taskNodes.forEach((t) => {
        if (t) {
          tasks.push({
            id: t.id,
            name: t.name,
            description: t.description || '',
          })
        }
      })
    }

    // Clean all references to prevent circular reference issues
    return this.create({
      creator: cleanUser(creator),
      name: data.name,
      description: data.description,
      url: url || '',
      type,
      workspace: cleanWorkspace(workspace),
      users,
      tasks: tasks as Task[],
      projects: projects as Project[],
      messages: [],
    })
  }

  /**
   * Get documents for workspace
   */
  async getDocumentsForWorkspace(workspaceId: string): Promise<Document[]> {
    return this.filter((document) => document.workspace.id === workspaceId)
  }

  /**
   * Get documents for user
   */
  async getDocumentsForUser(userId: string): Promise<Document[]> {
    return this.filter((document) => document.users.some((u) => u.id === userId))
  }

  /**
   * Get documents for task
   */
  async getDocumentsForTask(taskId: string): Promise<Document[]> {
    return this.filter((document) => document.tasks.some((t) => t.id === taskId))
  }

  /**
   * Get documents for project
   */
  async getDocumentsForProject(projectId: string): Promise<Document[]> {
    return this.filter((document) => document.projects.some((p) => p.id === projectId))
  }

  /**
   * Get documents by type
   */
  async getDocumentsByType(type: FileType): Promise<Document[]> {
    return this.filter((document) => document.type === type)
  }

  /**
   * Search documents
   */
  async searchDocuments(query: string): Promise<Document[]> {
    return this.search(query, ['name', 'description'])
  }

  /**
   * Share document with user
   */
  async shareWithUser(documentId: string, userId: string): Promise<Document> {
    const document = await this.getById(documentId)
    if (!document) {
      throw new Error('Document not found')
    }

    const { userService } = await import('./user.service')
    const user = await userService.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    if (document.users.some((u) => u.id === userId)) {
      throw new Error('Document already shared with user')
    }

    // Clean user to prevent circular reference issues
    return this.update(documentId, {
      users: [...document.users, cleanUser(user)],
    } as Partial<Document>)
  }

  /**
   * Unshare document from user
   */
  async unshareFromUser(documentId: string, userId: string): Promise<Document> {
    const document = await this.getById(documentId)
    if (!document) {
      throw new Error('Document not found')
    }

    // Can't unshare from creator
    if (document.creator.id === userId) {
      throw new Error('Cannot unshare from document creator')
    }

    return this.update(documentId, {
      users: document.users.filter((u) => u.id !== userId),
    } as Partial<Document>)
  }

  /**
   * Upload file (simulated for prototype)
   */
  private async uploadFile(file: File): Promise<string> {
    // In a real app, would upload to storage service
    // For prototype, generate mock URL
    return `/uploads/${Date.now()}_${file.name}`
  }

  /**
   * Get file type from File object
   */
  private getFileType(file: File): FileType {
    const mimeType = file.type

    if (mimeType.startsWith('image/')) return FileType.Image
    if (mimeType.startsWith('video/')) return FileType.Video
    if (mimeType.startsWith('audio/')) return FileType.Audio
    if (mimeType === 'application/pdf') return FileType.PDF
    if (mimeType.includes('document') || mimeType.includes('word')) return FileType.Document
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileType.Spreadsheet
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint'))
      return FileType.Presentation

    return FileType.File
  }

  /**
   * Get recent documents
   */
  async getRecentDocuments(limit: number = 10): Promise<Document[]> {
    const documents = await this.getAll()
    return documents
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limit)
  }

  /**
   * Get document stats
   */
  async getStats(documentId: string): Promise<{
    sharedWithCount: number
    linkedTasksCount: number
    linkedProjectsCount: number
  }> {
    const document = await this.getById(documentId)
    if (!document) {
      throw new Error('Document not found')
    }

    return {
      sharedWithCount: document.users.length,
      linkedTasksCount: document.tasks.length,
      linkedProjectsCount: document.projects.length,
    }
  }
}

export const documentService = new DocumentService()
